import { useEffect, useState } from 'react'
import {
  Title, Group, Card, Stack, Text, TextInput, Textarea, Button,
  Table, Badge, ActionIcon, Modal, LoadingOverlay, Paper,
  ColorInput, Tooltip,
} from '@mantine/core'
import {
  IconPlus, IconPencil, IconTrash, IconPhoto,
  IconArrowUp, IconArrowDown, IconEye, IconEyeOff, IconDeviceFloppy,
} from '@tabler/icons-react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmDelete, confirmAction } from '@/lib/confirm'
import { useDepartments } from '@/lib/swr'
import type { Department, DepartmentStat } from '@/types'

interface DepartmentFormData {
  name: string
  shortName: string
  slug: string
  description: string
  color: string
  colorEnd: string
  icon: string
  image: string
  stats: DepartmentStat[]
  phone: string
  location: string
  equipment: { value: string }[]
}

const EMPTY_FORM: DepartmentFormData = {
  name: '', shortName: '', slug: '',
  description: '',
  color: '#006C67', colorEnd: '#00897B',
  icon: '', image: '',
  stats: [],
  phone: '', location: '',
  equipment: [],
}

export default function DepartmentList() {
  const { data, isLoading, mutate } = useDepartments()
  const [departments, setDepartments] = useState<Department[]>([])
  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)

  useEffect(() => {
    const list: Department[] = (data as any)?.data ?? []
    setDepartments([...list].sort((a, b) => ((a as any).order ?? 0) - ((b as any).order ?? 0)))
    setOrderChanged(false)
  }, [data])

  const moveUp = (index: number) => {
    if (index === 0) return
    setDepartments((prev) => { const next = [...prev]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next })
    setOrderChanged(true)
  }
  const moveDown = (index: number) => {
    if (index === departments.length - 1) return
    setDepartments((prev) => { const next = [...prev]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; return next })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу? Энэ нь website дээр харагдах дарааллыг өөрчилнө.', async () => {
    setReordering(true)
    try {
      await api.admin.reorderDepartments(departments.map((d) => d._id))
      toast.success('Дараалал хадгалагдлаа')
      setOrderChanged(false); mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    } finally { setReordering(false) }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploadingIcon, setUploadingIcon] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<DepartmentFormData>({ defaultValues: EMPTY_FORM })

  const { fields: statFields, append: appendStat, remove: removeStat } = useFieldArray({ control, name: 'stats' })
  const { fields: equipmentFields, append: appendEquipment, remove: removeEquipment } = useFieldArray({ control, name: 'equipment' })

  const watchIcon = watch('icon')
  const watchImage = watch('image')

  const openCreate = () => {
    setEditId(null)
    reset(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = (dept: Department) => {
    setEditId(dept._id)
    reset({
      name: dept.name,
      shortName: dept.shortName,
      slug: dept.slug,
      description: dept.description,
      color: dept.color,
      colorEnd: dept.colorEnd,
      icon: dept.icon || '',
      image: dept.image || '',
      stats: dept.stats || [],
      phone: dept.phone,
      location: dept.location,
      equipment: (dept.equipment || []).map((v) => ({ value: v })),
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    reset(EMPTY_FORM)
  }

  const onSubmit = handleSubmit(async (form) => {
    try {
      const payload = {
        ...form,
        equipment: form.equipment.map((e) => e.value),
      }
      if (editId) {
        await api.admin.updateDepartment(editId, payload)
      } else {
        await api.admin.createDepartment(payload)
      }
      toast.success(editId ? 'Тасаг шинэчлэгдлээ' : 'Тасаг нэмэгдлээ')
      closeModal()
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const handleDelete = (id: string) => confirmDelete('Энэ тасгийг устгах уу?', async () => {
    try {
      await api.admin.deleteDepartment(id)
      toast.success('Тасаг устгагдлаа')
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const toggleStatus = async (dept: Department) => {
    try {
      await api.admin.updateDepartmentStatus(dept._id, !dept.isActive)
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  }

  const handleUpload = async (field: 'icon' | 'image', file: File) => {
    const setLoading = field === 'icon' ? setUploadingIcon : setUploadingImage
    setLoading(true)
    try {
      const result = await api.admin.uploadImage(file)
      setValue(field, result.url, { shouldDirty: true })
      toast.success('Зураг байршуулагдлаа')
    } catch {
      toast.error('Зураг байршуулахад алдаа гарлаа')
    } finally {
      setLoading(false)
    }
  }

  const pickFile = (field: 'icon' | 'image') => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = () => input.files?.[0] && handleUpload(field, input.files[0])
    input.click()
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Тасгууд</Title>
        <Group gap="xs">
          {orderChanged && (
            <Button variant="light" color="orange" leftSection={<IconDeviceFloppy size={16} />} onClick={saveOrder} loading={reordering}>Дараалал хадгалах</Button>
          )}
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Тасаг нэмэх</Button>
        </Group>
      </Group>

      <Card withBorder pos="relative">
        <LoadingOverlay visible={isLoading} />
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Өнгө</Table.Th>
              <Table.Th>Нэр</Table.Th>
              <Table.Th>Байршил</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th w={180}>Үйлдэл</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {departments.map((dept, idx) => (
              <Table.Tr key={dept._id}>
                <Table.Td>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: `linear-gradient(135deg, ${dept.color}, ${dept.colorEnd})` }} />
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>{dept.name}</Text>
                  <Text size="xs" c="dimmed">{dept.slug}</Text>
                </Table.Td>
                <Table.Td><Text size="sm">{dept.location || '—'}</Text></Table.Td>
                <Table.Td>
                  <Badge color={dept.isActive ? 'green' : 'gray'} variant="light" size="sm">
                    {dept.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <Tooltip label="Дээш" position="top" withArrow>
                      <ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === 0} onClick={() => moveUp(idx)}>
                        <IconArrowUp size={14} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Доош" position="top" withArrow>
                      <ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === departments.length - 1} onClick={() => moveDown(idx)}>
                        <IconArrowDown size={14} />
                      </ActionIcon>
                    </Tooltip>
                    <ActionIcon variant="light" size="sm" onClick={() => toggleStatus(dept)}>
                      {dept.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                    </ActionIcon>
                    <ActionIcon variant="light" size="sm" onClick={() => openEdit(dept)}>
                      <IconPencil size={14} />
                    </ActionIcon>
                    <ActionIcon variant="light" color="red" size="sm" onClick={() => handleDelete(dept._id)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {departments.length === 0 && !isLoading && (
              <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="lg">Тасаг бүртгэгдээгүй байна</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {/* Create/Edit Modal */}
      <Modal opened={modalOpen} onClose={closeModal} title={editId ? 'Тасаг засах' : 'Шинэ тасаг'} size="xl">
        <form onSubmit={onSubmit}>
          <Stack>
            <Group grow>
              <Controller
                name="name"
                control={control}
                rules={{ validate: (v) => (v.trim() ? true : 'Нэр шаардлагатай') }}
                render={({ field }) => (
                  <TextInput label="Нэр *" {...field} error={errors.name?.message} />
                )}
              />
              <Controller
                name="shortName"
                control={control}
                render={({ field }) => <TextInput label="Товч нэр" {...field} />}
              />
            </Group>
            <Controller
              name="slug"
              control={control}
              rules={{ validate: (v) => (v.trim() ? true : 'Slug шаардлагатай') }}
              render={({ field }) => (
                <TextInput label="Slug *" {...field} error={errors.slug?.message} />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => <Textarea label="Тайлбар" {...field} minRows={2} />}
            />

            <Group grow>
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <ColorInput label="Үндсэн өнгө" value={field.value} onChange={(v) => field.onChange(v)} />
                )}
              />
              <Controller
                name="colorEnd"
                control={control}
                render={({ field }) => (
                  <ColorInput label="Төгсгөл өнгө" value={field.value} onChange={(v) => field.onChange(v)} />
                )}
              />
            </Group>

            {/* Icon upload */}
            <div>
              <Text size="sm" fw={500} mb={4}>Icon</Text>
              <Group>
                <Controller
                  name="icon"
                  control={control}
                  render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />}
                />
                <Button size="sm" variant="light" leftSection={<IconPhoto size={14} />} loading={uploadingIcon} onClick={() => pickFile('icon')}>Upload</Button>
              </Group>
              {watchIcon && <img src={watchIcon} alt="" style={{ marginTop: 8, maxHeight: 40, objectFit: 'contain' }} />}
            </div>

            {/* Image upload */}
            <div>
              <Text size="sm" fw={500} mb={4}>Зураг</Text>
              <Group>
                <Controller
                  name="image"
                  control={control}
                  render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />}
                />
                <Button size="sm" variant="light" leftSection={<IconPhoto size={14} />} loading={uploadingImage} onClick={() => pickFile('image')}>Upload</Button>
              </Group>
              {watchImage && <img src={watchImage} alt="" style={{ marginTop: 8, maxHeight: 80, borderRadius: 8, objectFit: 'cover' }} />}
            </div>

            <Controller
              name="phone"
              control={control}
              render={({ field }) => <TextInput label="Утас" {...field} />}
            />
            <Controller
              name="location"
              control={control}
              render={({ field }) => <TextInput label="Байршил" {...field} />}
            />

            {/* Stats */}
            <Paper withBorder p="sm">
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>Мэдээлэл ({statFields.length})</Text>
                <Button size="xs" variant="subtle" onClick={() => appendStat({ label: '', value: '' })}>+ Нэмэх</Button>
              </Group>
              {statFields.map((sf, i) => (
                <Group key={sf.id} mb={4}>
                  <Controller
                    name={`stats.${i}.label` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput flex={1} size="xs" placeholder="Шошго (жнь: Ажилтан)" {...field} />
                    )}
                  />
                  <Controller
                    name={`stats.${i}.value` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput flex={1} size="xs" placeholder="Утга (жнь: 5 эмч, 8 физиотерапевт)" {...field} />
                    )}
                  />
                  <ActionIcon color="red" variant="light" size="sm" onClick={() => removeStat(i)}><IconTrash size={14} /></ActionIcon>
                </Group>
              ))}
            </Paper>

            {/* Equipment list */}
            <Paper withBorder p="sm">
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>Тоног төхөөрөмж ({equipmentFields.length})</Text>
                <Button size="xs" variant="subtle" onClick={() => appendEquipment({ value: '' })}>+ Нэмэх</Button>
              </Group>
              {equipmentFields.map((ef, i) => (
                <Group key={ef.id} mb={4}>
                  <Controller
                    name={`equipment.${i}.value` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput flex={1} size="xs" placeholder="Тоног төхөөрөмж" {...field} />
                    )}
                  />
                  <ActionIcon color="red" variant="light" size="sm" onClick={() => removeEquipment(i)}><IconTrash size={14} /></ActionIcon>
                </Group>
              ))}
            </Paper>

            <Group justify="flex-end">
              <Button variant="light" onClick={closeModal} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}
